// Colour arithmetic for the stem palette — Photon's theme::v2::deriveStemColor
// (cpopsa/src/stem_gui/Theme.hpp) and the S/M button fills
// (ModelSettingsViews.h SMButtonLookAndFeel), reproduced per channel.

import type { Hex } from '@/state/models';

export type Rgb = [number, number, number];

export function parseHex(hex: string): Rgb | null {
  const trimmed = hex.replace(/^#/, '').trim();
  if (trimmed.length !== 3 && trimmed.length !== 6) return null;
  const full =
    trimmed.length === 3
      ? trimmed.split('').map((c) => c + c).join('')
      : trimmed;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return null;
  return [r, g, b];
}

export function toHex(rgb: Rgb): Hex {
  return `#${rgb.map((c) => Math.round(Math.min(255, Math.max(0, c))).toString(16).padStart(2, '0')).join('')}`;
}

// Exact per-channel mix toward a grey level (0 = black, 255 = white), as
// deriveStemColor does it (juce::Colour::interpolatedWith drifts by ~2/channel).
export function mixToward(base: Rgb, target: number, t: number): Rgb {
  return base.map((c) => c + (target - c) * t) as Rgb;
}

// juce::Colour::withMultipliedBrightness(k): scales HSB brightness, which for
// RGB is a straight multiply of every channel (hue and saturation unchanged).
export function scaleBrightness(base: Rgb, k: number): Rgb {
  return base.map((c) => c * k) as Rgb;
}

export interface StemPalette {
  main: Hex;
  gradTop: Hex;        // stem dot gradient top: +30% toward white
  thumbFill: Hex;      // slider thumb: 48% brightness
  thumbRing: Hex;      // thumb ring: +70% toward white
  smFill: Hex;         // active S/M fill: ×0.60 (hover ×0.48, pressed ×0.38)
  smFillHover: Hex;
  smFillPressed: Hex;
}

export function stemPalette(main: Hex): StemPalette {
  const rgb = parseHex(main) ?? [0x80, 0x80, 0x80];
  return {
    main,
    gradTop: toHex(mixToward(rgb, 255, 0.30)),
    thumbFill: toHex(mixToward(rgb, 0, 0.52)),
    thumbRing: toHex(mixToward(rgb, 255, 0.70)),
    smFill: toHex(scaleBrightness(rgb, 0.60)),
    smFillHover: toHex(scaleBrightness(rgb, 0.48)),
    smFillPressed: toHex(scaleBrightness(rgb, 0.38)),
  };
}
